import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users,
  UserCheck,
  UserPlus,
  Shield,
  Activity,
  TrendingUp,
  Calendar,
  Clock,
  Mail,
  Phone,
  Building2,
  ArrowLeft,
  RefreshCw,
  Search,
  Filter,
  CheckCircle,
  XCircle,
  History,
  Gift,
  Sparkles,
  Package,
  MessageSquare,
  Receipt,
  Eye
} from 'lucide-react';
import { useAuth } from '../components/AuthProvider';
import api from '../services/api';
import UserDetailModal from '../components/UserDetailModal';

interface UserStats {
  total_users: number;
  active_users: number;
  admin_users: number;
  regular_users: number;
  users_today: number;
  users_this_week: number;
  users_this_month: number;
  online_users: number;
}

interface UserDetail {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_admin: boolean;
  is_active: boolean;
  phone: string;
  company: string;
  date_joined: string;
  last_login: string;
  last_activity: string;
  is_online: boolean;
  projects_count: number;
  invoices_count: number;
}

const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState<UserStats | null>(null);
  const [users, setUsers] = useState<UserDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'online' | 'admin' | 'active'>('all');
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | 'online' | 'offline' | 'history'>('all');
  const [selectedUser, setSelectedUser] = useState<UserDetail | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [globalHistory, setGlobalHistory] = useState<any[]>([]);
  const [historySearch, setHistorySearch] = useState('');

  useEffect(() => {
    loadUsersData();
    
    // Auto-refresh every 30 seconds for online status
    const interval = setInterval(() => {
      loadUsersData(true);
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Charger l'historique global quand on passe sur l'onglet historique
    if (activeTab === 'history' && globalHistory.length === 0) {
      loadGlobalHistory();
    }
  }, [activeTab]);

  const loadUsersData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setRefreshing(true);

      // Fetch statistics
      const statsResponse = await api.get('/api/users/stats/');
      setStats(statsResponse.data);

      // Fetch users list
      const usersResponse = await api.get('/api/users/list/');
      setUsers(usersResponse.data);

    } catch (error) {
      console.error('Erreur lors du chargement des données utilisateurs:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadGlobalHistory = async () => {
    try {
      const response = await api.get('/api/users/global-history/');
      setGlobalHistory(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement de l\'historique global:', error);
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = 
      u.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.company?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter = 
      filterStatus === 'all' ? true :
      filterStatus === 'online' ? u.is_online :
      filterStatus === 'admin' ? u.is_admin :
      filterStatus === 'active' ? u.is_active : true;

    const matchesTab = 
      activeTab === 'all' ? true :
      activeTab === 'online' ? u.is_online :
      activeTab === 'offline' ? !u.is_online : true;

    return matchesSearch && matchesFilter && matchesTab;
  });

  const onlineUsers = users.filter(u => u.is_online);
  const offlineUsers = users.filter(u => !u.is_online);

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Jamais';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!user?.isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Accès non autorisé</h2>
          <p className="text-gray-600">Vous devez être administrateur pour accéder à cette page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-blue-600 mx-auto mb-4 animate-spin" />
          <p className="text-gray-600">Chargement des données utilisateurs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-700" />
              </button>
              <div className="flex items-center space-x-3">
                <Users className="w-8 h-8 text-blue-600" />
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Gestion des Utilisateurs</h1>
                  <p className="text-sm text-gray-600">Statistiques et liste complète des utilisateurs</p>
                </div>
              </div>
            </div>

            <button
              onClick={() => loadUsersData()}
              disabled={refreshing}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span>Actualiser</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Statistics Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Utilisateurs</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total_users}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {stats.admin_users} admins • {stats.regular_users} clients
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">En ligne</p>
                  <p className="text-3xl font-bold text-green-600">{stats.online_users}</p>
                  <div className="flex items-center space-x-1 mt-1">
                    <Activity className="w-3 h-3 text-green-500" />
                    <p className="text-xs text-green-600">Actifs (5 min)</p>
                  </div>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <UserCheck className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Nouveaux ce mois</p>
                  <p className="text-3xl font-bold text-purple-600">{stats.users_this_month}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Cette semaine: {stats.users_this_week}
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <UserPlus className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Nouveaux aujourd'hui</p>
                  <p className="text-3xl font-bold text-amber-600">{stats.users_today}</p>
                  <div className="flex items-center space-x-1 mt-1">
                    <TrendingUp className="w-3 h-3 text-amber-500" />
                    <p className="text-xs text-amber-600">Croissance</p>
                  </div>
                </div>
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm mb-6 border border-gray-200 overflow-hidden">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('all')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'all'
                  ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <Users className="w-5 h-5" />
                <span>Tous les utilisateurs</span>
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  activeTab === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'
                }`}>
                  {users.length}
                </span>
              </div>
            </button>

            <button
              onClick={() => setActiveTab('online')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'online'
                  ? 'bg-green-50 text-green-700 border-b-2 border-green-600'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <Activity className="w-5 h-5" />
                <span>En ligne</span>
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  activeTab === 'online' ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700'
                }`}>
                  {onlineUsers.length}
                </span>
              </div>
            </button>

            <button
              onClick={() => setActiveTab('offline')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'offline'
                  ? 'bg-gray-100 text-gray-700 border-b-2 border-gray-600'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <UserCheck className="w-5 h-5" />
                <span>Hors ligne</span>
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  activeTab === 'offline' ? 'bg-gray-600 text-white' : 'bg-gray-200 text-gray-700'
                }`}>
                  {offlineUsers.length}
                </span>
              </div>
            </button>

            <button
              onClick={() => setActiveTab('history')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'history'
                  ? 'bg-purple-50 text-purple-700 border-b-2 border-purple-600'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <History className="w-5 h-5" />
                <span>Historique</span>
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  activeTab === 'history' ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-700'
                }`}>
                  {globalHistory.length}
                </span>
              </div>
            </button>
          </div>
        </div>

        {/* Filters and Search */}
        {activeTab !== 'history' && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-gray-200">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Rechercher par nom, email, entreprise..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex items-center space-x-2">
                <Filter className="w-5 h-5 text-gray-400" />
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value as any)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">Tous les utilisateurs</option>
                  <option value="online">En ligne</option>
                  <option value="admin">Administrateurs</option>
                  <option value="active">Actifs</option>
                </select>
              </div>
            </div>

            <div className="mt-4 flex items-center space-x-2 text-sm text-gray-600">
              <span className="font-medium">{filteredUsers.length}</span>
              <span>utilisateur{filteredUsers.length > 1 ? 's' : ''} trouvé{filteredUsers.length > 1 ? 's' : ''}</span>
            </div>
          </div>
        )}

        {/* History Search */}
        {activeTab === 'history' && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Rechercher par utilisateur, email, numéro de facture, code promo..."
                value={historySearch}
                onChange={(e) => setHistorySearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div className="mt-4 flex items-center space-x-2 text-sm text-gray-600">
              <span className="font-medium">{globalHistory.filter(item => 
                item.user_name.toLowerCase().includes(historySearch.toLowerCase()) ||
                item.user_email.toLowerCase().includes(historySearch.toLowerCase()) ||
                item.description.toLowerCase().includes(historySearch.toLowerCase()) ||
                (item.metadata?.invoice_number && item.metadata.invoice_number.toLowerCase().includes(historySearch.toLowerCase())) ||
                (item.metadata?.code && item.metadata.code.toLowerCase().includes(historySearch.toLowerCase()))
              ).length}</span>
              <span>événement{globalHistory.length > 1 ? 's' : ''} trouvé{globalHistory.length > 1 ? 's' : ''}</span>
            </div>
          </div>
        )}

        {/* Users Table */}
        {activeTab !== 'history' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Utilisateur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rôle
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Activité
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stats
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((u) => (
                  <motion.tr
                    key={u.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    onClick={() => {
                      setSelectedUser(u);
                      setShowModal(true);
                    }}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold">
                          {u.first_name.charAt(0)}{u.last_name.charAt(0)}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {u.first_name} {u.last_name}
                          </div>
                          <div className="text-sm text-gray-500">@{u.username}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {u.is_admin ? (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                          <Shield className="w-3 h-3 mr-1" />
                          Administrateur
                        </span>
                      ) : (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          Client
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 flex items-center space-x-1">
                        <Mail className="w-3 h-3 text-gray-400" />
                        <span>{u.email}</span>
                      </div>
                      {u.phone && (
                        <div className="text-sm text-gray-500 flex items-center space-x-1">
                          <Phone className="w-3 h-3 text-gray-400" />
                          <span>{u.phone}</span>
                        </div>
                      )}
                      {u.company && (
                        <div className="text-sm text-gray-500 flex items-center space-x-1">
                          <Building2 className="w-3 h-3 text-gray-400" />
                          <span>{u.company}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-3 h-3" />
                        <span>Inscrit: {formatDate(u.date_joined)}</span>
                      </div>
                      <div className="flex items-center space-x-1 mt-1">
                        <Clock className="w-3 h-3" />
                        <span>Dernière activité: {formatDate(u.last_activity || u.last_login)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col space-y-1">
                        {u.is_online ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <Activity className="w-3 h-3 mr-1" />
                            En ligne
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            Hors ligne
                          </span>
                        )}
                        {u.is_active ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Actif
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <XCircle className="w-3 h-3 mr-1" />
                            Inactif
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <div>Projets: <span className="font-semibold">{u.projects_count || 0}</span></div>
                      <div>Factures: <span className="font-semibold">{u.invoices_count || 0}</span></div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        )}

        {/* History Display */}
        {activeTab === 'history' && (
          <div className="space-y-4">
            {globalHistory
              .filter(item => 
                item.user_name.toLowerCase().includes(historySearch.toLowerCase()) ||
                item.user_email.toLowerCase().includes(historySearch.toLowerCase()) ||
                item.description.toLowerCase().includes(historySearch.toLowerCase()) ||
                (item.metadata?.invoice_number && item.metadata.invoice_number.toLowerCase().includes(historySearch.toLowerCase())) ||
                (item.metadata?.code && item.metadata.code.toLowerCase().includes(historySearch.toLowerCase()))
              )
              .map((item) => {
                const getIcon = (type: string) => {
                  switch (type) {
                    case 'promo': return <Gift className="w-4 h-4" />;
                    case 'invoice': return <Receipt className="w-4 h-4" />;
                    case 'project': return <Package className="w-4 h-4" />;
                    case 'wheel': return <Sparkles className="w-4 h-4" />;
                    default: return <History className="w-4 h-4" />;
                  }
                };

                const getColor = (type: string) => {
                  switch (type) {
                    case 'promo': return 'bg-pink-100 text-pink-800';
                    case 'invoice': return 'bg-green-100 text-green-800';
                    case 'project': return 'bg-purple-100 text-purple-800';
                    case 'wheel': return 'bg-amber-100 text-amber-800';
                    default: return 'bg-gray-100 text-gray-800';
                  }
                };

                return (
                  <div key={item.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1">
                        <div className={`p-2 rounded-lg ${getColor(item.type)}`}>
                          {getIcon(item.type)}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="font-semibold text-gray-900">{item.user_name}</span>
                            <span className="text-xs text-gray-500">({item.user_email})</span>
                          </div>
                          <p className="text-sm text-gray-900">{item.description}</p>
                          <p className="text-xs text-gray-500 mt-1">{formatDate(item.timestamp)}</p>
                        </div>
                      </div>
                      {item.type === 'invoice' && item.metadata?.invoice_id && (
                        <button
                          onClick={() => {
                            navigate('/facture-proforma', {
                              state: {
                                purchaseRequestId: item.metadata.invoice_id,
                                viewMode: true
                              }
                            });
                          }}
                          className="ml-3 flex items-center space-x-1 px-2 py-1 bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200 transition-colors text-xs font-medium"
                          title="Voir la facture"
                        >
                          <Eye className="w-3 h-3" />
                          <span>Voir</span>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {/* Modal détails utilisateur */}
      {selectedUser && (
        <UserDetailModal
          user={selectedUser}
          isOpen={showModal}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
};

export default UsersPage;
